import React, { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import ShareButton from './ShareButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default partners shown when database is empty
const defaultPartners = [
  {
    name: 'Expedia',
    logo: 'https://www.expedia.com/_dms/header/logo.svg?locale=en_US&siteid=1&2',
    description: 'Book flights, hotels, and vacation packages',
    affiliateLink: 'https://www.expedia.com',
    category: 'Booking Platform'
  },
  {
    name: 'Exoticca',
    logo: 'https://res.cloudinary.com/exoticca/image/upload/f_auto,q_auto/v1/website/logos/exoticca-logo-black.svg',
    description: 'Exclusive multi-destination tours',
    affiliateLink: 'https://www.exoticca.com',
    category: 'Tour Operator'
  },
  {
    name: 'Context Travel',
    logo: 'https://www.contexttravel.com/assets/images/logo.png',
    description: 'In-depth cultural walking tours',
    affiliateLink: 'https://www.contexttravel.com',
    category: 'Cultural Tours'
  },
  {
    name: 'Viator',
    logo: 'https://hare-media-cdn.tripadvisor.com/media/attractions-splice-spp-674x446/11/83/7e/d1.jpg',
    description: 'Tours, activities, and experiences',
    affiliateLink: 'https://www.viator.com',
    category: 'Activities'
  },
  {
    name: 'GetYourGuide',
    logo: 'https://cdn.getyourguide.com/tf/assets/static/logos/gyg-logo-blue.svg',
    description: 'Discover and book tours worldwide',
    affiliateLink: 'https://www.getyourguide.com',
    category: 'Activities'
  },
  {
    name: 'Booking.com',
    logo: 'https://cf.bstatic.com/static/img/booking_logo_knowledge_graph/247454a990efac1952e44dddbf30c58677aa0fd8.png',
    description: 'Hotels and accommodations worldwide',
    affiliateLink: 'https://www.booking.com',
    category: 'Accommodations'
  }
];

const Partners = () => {
  const [partners, setPartners] = useState(defaultPartners);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await fetch(`${API_URL}/api/partners`);
        if (response.ok) {
          const data = await response.json();
          // Only use database partners if there are any, otherwise keep defaults
          if (data.partners && data.partners.length > 0) {
            // Filter to only show active partners
            const activePartners = data.partners.filter(p => p.active !== false);
            if (activePartners.length > 0) {
              setPartners(activePartners);
            }
          }
        }
      } catch (error) {
        console.log('Using default partners');
      } finally {
        setLoading(false);
      }
    };

    fetchPartners();
  }, []);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-black text-black mb-4">
            OUR TRUSTED <span className="text-[#84bc00]">PARTNERS</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We partner with the world's leading travel platforms to bring you the best experiences at the best prices.
          </p>
        </div>

        {/* Partners Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {partners.map((partner, index) => (
            <a
              key={partner.partner_id || index}
              href={partner.affiliateLink}
              target="_blank"
              rel="noopener noreferrer sponsored"
              className="group bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-[#84bc00] hover:shadow-2xl transition-all duration-300 hover:scale-105"
              data-testid={`partner-card-${partner.name?.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex flex-col h-full">
                {/* Logo */}
                <div className="h-20 flex items-center justify-center mb-6 bg-gray-50 rounded-lg p-4">
                  <img 
                    src={partner.logo} 
                    alt={partner.name}
                    className="max-h-16 max-w-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div style={{display: 'none'}} className="text-2xl font-black text-gray-700">
                    {partner.name}
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div className="mb-3">
                    <span className="inline-block bg-[#84bc00]/10 text-[#84bc00] text-xs font-bold px-3 py-1 rounded-full">
                      {partner.category}
                    </span>
                  </div>
                  <h3 className="text-xl font-black text-black mb-2 group-hover:text-[#84bc00] transition-colors">
                    {partner.name}
                  </h3>
                  <p className="text-gray-600 mb-4 text-base">
                    {partner.description}
                  </p>
                </div>

                {/* Link */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-[#84bc00] font-bold text-sm group-hover:translate-x-2 transition-transform duration-300">
                    <span>Visit Partner</span>
                    <ExternalLink size={16} className="ml-2" />
                  </div>
                  
                  <ShareButton 
                    title={`${partner.name} - Travel Partner`}
                    description={partner.description}
                    url={partner.affiliateLink}
                  />
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* Disclaimer */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500 italic">
            * Some links are affiliate partnerships. We may earn a commission at no extra cost to you.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Partners;
